import React, { useEffect, useState } from 'react';
import { RefreshCw, Loader2, CheckCircle2, AlertCircle, Lock } from 'lucide-react';
import { atlasApi, AtlasUser } from '../shared/atlasApi';

interface ConfigProps {
  user: AtlasUser;
  isAdmin: boolean;
}

const Configuracoes: React.FC<ConfigProps> = ({ user, isAdmin }) => {
  const [status, setStatus] = useState<any>(null);
  const [health, setHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [disparando, setDisparando] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const [h, s] = await Promise.all([
        atlasApi.health().catch(() => null),
        atlasApi.syncStatus(user).catch(() => null),
      ]);
      setHealth(h);
      setStatus(s);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  // Auto-refresh quando sync está rodando
  useEffect(() => {
    if (!status?.rodando) return;
    const i = setInterval(() => atlasApi.syncStatus(user).then(setStatus).catch(() => null), 3000);
    return () => clearInterval(i);
  }, [status?.rodando, user]);

  const dispararSync = async () => {
    setDisparando(true);
    setFeedback(null);
    try {
      await atlasApi.dispararSync(user);
      setFeedback('Sincronização iniciada. Atualizando status...');
      setTimeout(carregar, 1500);
    } catch (e: any) {
      setFeedback(`Erro: ${e.message || 'falha desconhecida'}`);
    } finally {
      setDisparando(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      {/* Saúde do módulo */}
      <section className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Saúde do módulo</h3>
        <dl className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-gray-500">MongoDB</dt>
            <dd>{health?.mongo ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <AlertCircle className="w-4 h-4 text-rose-500" />}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-gray-500 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Criptografia LGPD (Fernet)</dt>
            <dd>{health?.fernet ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <AlertCircle className="w-4 h-4 text-amber-500" />}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-gray-500">Sincronização automática</dt>
            <dd className={`text-xs font-medium ${health?.sync_auto ? 'text-emerald-600' : 'text-gray-400'}`}>
              {health?.sync_auto ? 'ativada' : 'desativada'}
            </dd>
          </div>
        </dl>
        {!health?.fernet && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            Fernet desativado: o servidor não tem ATLAS_FERNET_KEY no ambiente. CPF e salário não serão criptografados.
          </div>
        )}
      </section>

      {/* Sync GIX */}
      <section className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Sincronização GIX → Atlas</h3>
            <p className="text-xs text-gray-500 mt-1">
              Importa funcionários do ERP de RH (PostgreSQL). Cada execução faz upsert sem apagar dados Mongo.
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={dispararSync}
              disabled={disparando || status?.rodando}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {(disparando || status?.rodando) ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sincronizar agora
            </button>
          )}
        </div>

        {feedback && <div className="mb-3 text-xs text-purple-700">{feedback}</div>}

        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-gray-500">Estado atual</dt>
          <dd className={`font-medium ${status?.rodando ? 'text-purple-600' : 'text-gray-700'}`}>
            {status?.rodando ? 'Em execução...' : 'Ocioso'}
          </dd>
          <dt className="text-gray-500">Última execução</dt>
          <dd className="text-gray-700">
            {status?.ultima_execucao ? new Date(status.ultima_execucao).toLocaleString('pt-BR') : '—'}
          </dd>
          <dt className="text-gray-500">Resultado</dt>
          <dd className={`font-medium ${status?.ultimo_resultado === 'erro' ? 'text-rose-600' : 'text-emerald-600'}`}>
            {status?.ultimo_resultado || '—'}
          </dd>
          <dt className="text-gray-500">Duração</dt>
          <dd className="text-gray-700">{status?.ultima_duracao_seg ? `${status.ultima_duracao_seg}s` : '—'}</dd>
          <dt className="text-gray-500">Inseridos / Atualizados</dt>
          <dd className="text-gray-700">{status?.total_inseridos ?? 0} / {status?.total_atualizados ?? 0}</dd>
        </dl>

        {status?.ultimo_erro && (
          <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700">
            Último erro: {status.ultimo_erro}
          </div>
        )}

        {!isAdmin && (
          <p className="mt-3 text-xs text-gray-400 italic">
            Apenas administradores podem disparar a sincronização.
          </p>
        )}
      </section>
    </div>
  );
};

export default Configuracoes;
